/**
 * O Context, os tipos, o storage e o hook da loja do filtro de menções —
 * tudo o que NÃO é componente, separado do provedor de propósito.
 *
 * **Por que este arquivo existe à parte de `mention-filter-store.tsx`**
 * (2026-10-25, achado 26 da revisão de 2026-10-24; a mesma receita que
 * separou `app-shell-context.ts` do `AppShellProvider` em 2026-10-19): o
 * Fast Refresh do Vite só troca um módulo a quente, preservando o que
 * está montado, quando ele exporta SÓ componentes. Com o hook exportado
 * ao lado do provedor, qualquer edição que chegasse ao arquivo — a
 * própria, ou a de `mention-filter-criteria.ts`, que ele importa e que
 * muda com frequência — o reexecutava inteiro, o `createContext` rodava
 * de novo, e o provedor montado passava a escrever num Context enquanto
 * as telas liam em outro. Aqui a falha era PIOR que na casca, porque é
 * silenciosa: `useMentionFilterStore` cai no estado local sem erro, e
 * Menções, Relatório e Justificativas param de compartilhar o filtro até
 * um F5. `shell/fast-refresh-boundary.test.ts` guarda a fronteira dos
 * dois provedores.
 *
 * A decisão de produto e o desenho da loja estão no cabeçalho de
 * `mention-filter-store.tsx`.
 */

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  criteriaFromState,
  emptyFilters,
  resolveFacetIds,
  stateFromCriteria,
  type IdDimension,
  type MentionFilterCriteria,
  type MentionFilters,
  type SavedFacetIds,
} from "@/features/mentions/mention-filter-criteria";
import {
  MENTION_SEARCH_FIELDS,
  type MentionSearchField,
} from "@/shared/vocabulary/mention-search-fields";

/** De quem são as menções da lista — o `scope` do servidor. */
export type MentionListScope = "own" | "competitors" | "all";

/** A lista colada (2026-10-03), depois de o servidor a resolver e
 *  GUARDAR: o número dela (é o que filtra), quantas publicações achou,
 *  a amostra das referências sem par com o total, e quando vence.
 *  Efêmera como o filtro de upload — fora do filtro salvo —, mas VIAJA
 *  pro relatório (decisão da dona). */
export type PastedReferences = {
  setId: number;
  postCount: number;
  unmatched: string[];
  unmatchedCount: number;
  expiresAt: number;
  /** O texto que foi resolvido, e em que escopo. A busca dos links
   *  segue o escopo da tela (decisão da dona, 2026-10-04): trocar
   *  "Menções de" com a lista ativa a resolve de novo no escopo novo, e
   *  é com ESTE texto, não com o rascunho, que a pessoa pode ter
   *  editado depois. */
  text: string;
  scope: MentionListScope;
};

/** O que as duas telas dividem. */
export type SharedMentionFilters = {
  filters: MentionFilters;
  query: string;
  searchIn: MentionSearchField;
  scope: MentionListScope;
  refFilter: PastedReferences | null;
  /** Os ids que um filtro salvo (ou um recorte de relatório) trouxe, por
   *  dimensão e nome. A consulta os usa pra nome que o catálogo vivo não
   *  conhece — tema renomeado, desativado, ou catálogo ainda não
   *  carregado. */
  savedIds: SavedFacetIds;
};

export const emptySharedFilters = (): SharedMentionFilters => ({
  filters: emptyFilters(),
  query: "",
  searchIn: "all",
  scope: "own",
  refFilter: null,
  savedIds: {},
});

/** Nome → ids de uma dimensão de catálogo. O hook registra aqui o seu
 *  resolvedor (catálogo vivo + ids guardados) pra o provedor gravar a
 *  mesma coisa que a consulta mandaria. */
export type IdResolverFn = (
  dimension: IdDimension,
  names: Set<string>
) => number[];

export type StoreValue = {
  state: SharedMentionFilters;
  /** A empresa nunca teve filtro neste navegador (2026-10-26): é a
   *  primeira visita, e a tela de menções abre com a janela de data
   *  padrão. Vira `false` na primeira escrita — de quem for — e fica
   *  assim (ver `markVisited`). Sem provedor é sempre `false`. */
  fresh: boolean;
  setState: (
    update: (current: SharedMentionFilters) => SharedMentionFilters
  ) => void;
  companyId: string | null;
  resolverRef: MutableRefObject<IdResolverFn | null>;
};

export const StoreContext = createContext<StoreValue | null>(null);

const STORAGE_PREFIX = "reputaition.mention_filters.";
const STORAGE_VERSION = 1;
const VISITED_PREFIX = "reputaition.mention_filters_seen.";
const SCOPES: readonly MentionListScope[] = ["own", "competitors", "all"];

export function mentionFiltersStorageKey(companyId: string | null): string {
  return `${STORAGE_PREFIX}${companyId ?? "none"}`;
}

/** A empresa já teve filtro neste navegador? Dois rastros valem: o
 *  filtro guardado e a MARCA de visita. A marca existe porque filtro
 *  limpo não deixa rastro no storage (ver `writeStored`), e "limpou tudo,
 *  inclusive o período" é uma escolha que a janela padrão não pode
 *  desfazer na visita seguinte. Storage ilegível conta como já visitada:
 *  sem storage a janela não se lembraria de nada, e impô-la a cada
 *  abertura seria desfazer a escolha da pessoa. */
export function hasVisited(companyId: string | null): boolean {
  try {
    return (
      window.localStorage.getItem(`${VISITED_PREFIX}${companyId ?? "none"}`) !== null ||
      window.localStorage.getItem(mentionFiltersStorageKey(companyId)) !== null
    );
  } catch {
    return true;
  }
}

export function markVisited(companyId: string | null): void {
  try {
    window.localStorage.setItem(`${VISITED_PREFIX}${companyId ?? "none"}`, "1");
  } catch {
    // Sem storage: vale só nesta aba.
  }
}

type Stored = {
  v: number;
  criteria: MentionFilterCriteria;
  scope?: MentionListScope;
};

/** Lê o que a empresa deixou no navegador, ou o estado vazio. Sem
 *  catálogo aqui: os itens `{label, ids}` voltam como nome + `savedIds`,
 *  que é o caminho que o hook já usa pra nome que o catálogo não
 *  resolve — e o relabel de `#<id>` troca pelo nome de hoje quando o
 *  catálogo chega. */
export function readStored(companyId: string | null): SharedMentionFilters {
  const empty = emptySharedFilters();
  try {
    const raw = window.localStorage.getItem(mentionFiltersStorageKey(companyId));
    if (!raw) return empty;
    const stored = JSON.parse(raw) as Stored;
    if (stored?.v !== STORAGE_VERSION || !stored.criteria) return empty;
    const restored = stateFromCriteria(stored.criteria, { showsCena: true });
    const queryIn: MentionSearchField =
      restored.queryIn && MENTION_SEARCH_FIELDS.includes(restored.queryIn)
        ? restored.queryIn
        : "all";
    return {
      ...empty,
      filters: restored.filters,
      query: restored.query,
      searchIn: queryIn,
      scope: stored.scope && SCOPES.includes(stored.scope) ? stored.scope : "own",
      savedIds: restored.savedIds ?? {},
    };
  } catch {
    // Storage ilegível ou JSON torto: começa vazio.
    return empty;
  }
}

export function writeStored(
  companyId: string | null,
  state: SharedMentionFilters,
  resolver: IdResolverFn | null
): void {
  try {
    const resolve: IdResolverFn =
      resolver ??
      ((dimension, names) =>
        resolveFacetIds(names, null, state.savedIds[dimension]));
    const criteria = criteriaFromState(
      { query: state.query, queryIn: state.searchIn, filters: state.filters },
      resolve
    );
    const key = mentionFiltersStorageKey(companyId);
    // Estado vazio não deixa rastro no storage.
    const empty =
      Object.keys(criteria).length === 1 && state.scope === "own";
    if (empty) window.localStorage.removeItem(key);
    else
      window.localStorage.setItem(
        key,
        JSON.stringify({ v: STORAGE_VERSION, criteria, scope: state.scope })
      );
  } catch {
    // Sem storage (janela privada, cota): o filtro vale só nesta aba.
  }
}

/**
 * O estado compartilhado. **Sem provedor devolve um estado local** (um
 * `useState` por chamada) — o `useState` é chamado sempre, pra a ordem
 * dos hooks não depender de haver ou não provedor.
 */
export function useMentionFilterStore(): StoreValue {
  const shared = useContext(StoreContext);
  const [local, setLocal] = useState<SharedMentionFilters>(emptySharedFilters);
  const localResolver = useRef<IdResolverFn | null>(null);
  const fallback = useMemo<StoreValue>(
    () => ({
      state: local,
      fresh: false,
      setState: setLocal,
      companyId: null,
      resolverRef: localResolver,
    }),
    [local]
  );
  return shared ?? fallback;
}
